import { useState, useCallback, useEffect, useMemo } from 'react';

export default function useAdvancedLegend() {
  const [series, setSeries] = useState({
    active: null,
    hovered: null,
  });

  const handleClick = useCallback((item) => {
    setSeries((prevSeries) => ({
      ...prevSeries,
      active: prevSeries.active?.id === item.id ? null : item,
    }));
  }, []);

  const handleMouseOver = useCallback((item) => {
    setSeries((prevSeries) => {
      // Don't update hovered state if the item is active or already hovered to prevent unnecessary re-renders.
      if (prevSeries.active || prevSeries.hovered?.id === item.id) return prevSeries;

      return {
        ...prevSeries,
        hovered: item,
      };
    });
  }, []);

  const handleMouseOut = useCallback(() => {
    setSeries((prevSeries) => ({
      ...prevSeries,
      hovered: null,
    }));
  }, []);

  const classesToAdd = useCallback(
    (key) => {
      const { active, hovered } = series;
      return (
        (active?.id && (active.id === key ? 'active-series' : 'inactive-series')) ||
        (hovered?.id && (hovered.id === key ? 'hovered-series' : 'inactive-series'))
      );
    },
    [series]
  );

  return {
    activeItem: series.active,
    hoveredItem: series.hovered,
    onClick: handleClick,
    onMouseOver: handleMouseOver,
    onMouseOut: handleMouseOut,
    classesToAdd,
  };
}

export function useToggleSeriesVisibility(chartRef, activeItem, isSeriesVisible) {
  // Toggle series visibility on activeItem change to leverage built-in animation and avoid re-rendering the whole chart.
  useEffect(() => {
    const chart = chartRef.current?.chartAPIRef();
    if (!chart) return;

    chart.series.forEach((s) => {
      const shouldShow = isSeriesVisible
        ? isSeriesVisible(s.options.id, activeItem)
        : !activeItem || activeItem.id === s.options.id;
      if (s.visible !== shouldShow) s.setVisible(shouldShow, false);
    });
    chart.redraw(true); // single animated redraw at the end
  }, [activeItem, chartRef, isSeriesVisible]);
}

export function useHoveredItemClassName(series, hoveredItem, seriesKeys) {
  // Need for track and style hover series state
  const mapSeriesKeyToIndex = useMemo(() => {
    const keys = (seriesKeys || Object.keys(series)).filter((key) => series[key]?.length);

    return keys.reduce((acc, key, index) => {
      acc[key] = index;
      return acc;
    }, {});
  }, [series, seriesKeys]);

  return hoveredItem ? `hovered-${mapSeriesKeyToIndex[hoveredItem.id]}` : '';
}
